import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { Button, Card, CardContent } from '@/components/ui';
import { PublicPlayEventCard } from '@/components/public/play/PublicPlayEventCard';
import { useTenantPrimaryColor } from '@/contexts/AcademyBrandingContext';
import { playEventService, type PublicPlayEventSummary } from '@/services/playEvent';
import { cn } from '@/lib/utils';

interface OrgPlayEventsSectionProps {
  orgSlug: string;
  orgPath: (subpath: string) => string | null;
  playEventsEntitled: boolean;
}

const PREVIEW_LIMIT = 3;

export function OrgPlayEventsSection({
  orgSlug,
  orgPath,
  playEventsEntitled,
}: OrgPlayEventsSectionProps) {
  const accent = useTenantPrimaryColor();
  const navigate = useNavigate();
  const [events, setEvents] = useState<PublicPlayEventSummary[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!playEventsEntitled || !orgSlug) {
      return undefined;
    }
    let cancelled = false;
    const loadEvents = async () => {
      try {
        const allRes = await playEventService.listPublicEvents(orgSlug);
        const items = allRes.success && allRes.data ? allRes.data : [];
        if (cancelled) return;
        setEvents(items.slice(0, PREVIEW_LIMIT));
      } catch {
        if (cancelled) return;
        setEvents([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    void loadEvents();
    return () => {
      cancelled = true;
    };
  }, [orgSlug, playEventsEntitled]);

  const { featuredEvents, otherEvents } = useMemo(() => {
    const featured = events.filter((event) => event.isFeatured);
    const featuredIds = new Set(featured.map((event) => event.id));
    const other = events.filter((event) => !featuredIds.has(event.id));
    return { featuredEvents: featured, otherEvents: other };
  }, [events]);

  if (!playEventsEntitled) return null;

  const playEventsPath = orgPath('/play');
  if (!playEventsPath) return null;

  const openEvent = (slug: string) => {
    navigate(`${playEventsPath}?event=${encodeURIComponent(slug)}`);
  };

  const gridClassName = cn('grid gap-4', otherEvents.length >= 2 && 'sm:grid-cols-2 lg:grid-cols-3');

  return (
    <section aria-labelledby="play-events-heading" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <h2 id="play-events-heading" className="text-2xl font-bold text-foreground">
          Play events
        </h2>
        <Button asChild variant="outline" size="sm">
          <Link to={playEventsPath}>View all events</Link>
        </Button>
      </div>

      {!loaded ? (
        <Card>
          <CardContent className="flex min-h-[120px] items-center justify-center py-8" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </CardContent>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <Trophy className="mt-0.5 h-6 w-6 shrink-0" style={{ color: accent }} aria-hidden />
              <div>
                <h3 className="text-lg font-semibold text-foreground">Social play & tournaments</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  No play sessions are scheduled right now. Check back soon for social play,
                  competitions, and open court events.
                </p>
              </div>
            </div>
            <Button asChild style={{ backgroundColor: accent, borderColor: accent }}>
              <Link to={playEventsPath}>Browse events</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {featuredEvents.length > 0 ? (
            <ul className="flex flex-col gap-4" aria-label="Featured play events">
              {featuredEvents.map((event) => (
                <li key={event.id}>
                  <PublicPlayEventCard
                    event={event}
                    featured
                    fullWidth
                    onOpen={() => openEvent(event.slug)}
                  />
                </li>
              ))}
            </ul>
          ) : null}

          {otherEvents.length > 0 ? (
            <ul className={gridClassName} aria-label="Play events">
              {otherEvents.map((event) => (
                <li key={event.id}>
                  <PublicPlayEventCard event={event} onOpen={() => openEvent(event.slug)} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </section>
  );
}
